import { useEffect, useMemo, useRef, useState } from 'react'
import type { DeviceSpecDevice, DeviceSpecTag } from '../../types'
import { channelDef } from './catalog'
import DeviceDrawer from './DeviceDrawer'
import FindReplace from './FindReplace'
import type { FindMode } from './FindReplace'
import { selectionLabel, toFindSelection, useOutsideClose } from './findScope'
import type { FindHit, FindSelection } from './findScope'
import { ViewerAvatars, ViewerChips } from './Presence'
import { cellMarks } from './presenceMarks'
import SaveBadge from './SaveBadge'
import SheetStatus from './SheetStatus'
import type { CellNote } from './SheetStatus'
import TabMenu from './TabMenu'
import TagGrid from './TagGrid'
import type { TagGridHandle } from './TagGrid'
import type { IssueSpot, SpecIssues } from './model'
import { deviceIssueCount, emptyDevice, emptyTag, endpointText, issueSpots, nextSpot, normalizeAddress } from './model'
import { setCellInput } from './recompute'
import { duplicateDevice, findDuplicates, moveDevice } from './specOps'
import type { SpecDraft } from './useSpecDraft'
import { useSheetTabs } from './useSheetTabs'
import { hasAddress } from './useSpecDraft'

// 편집 모드 — 엑셀처럼 시트만 화면 가득. 위 툴바(되돌리기·행 조작·찾기/바꾸기·중복 표시·점검(이전·다음 문제)·수식 도움말),
// 시트 아래 상태 줄(지금 칸이 붉은·노란 이유 + 범례 — SheetStatus),
// 아래 탭 = 디바이스 (더블클릭 이름 변경, 우클릭 메뉴, 끌어서 순서 변경, Alt+↑/↓ 전환 — useSheetTabs). 자동 저장.

const TEXT_FIELDS = ['address', 'size', 'arithmetic', 'description', 'alias'] as const
type TextField = typeof TEXT_FIELDS[number]
const FIND_COLUMNS = [
  { key: 'all', label: '모든 열' }, { key: 'address', label: '주소' }, { key: 'size', label: '크기' },
  { key: 'arithmetic', label: '연산' }, { key: 'description', label: '설명' }, { key: 'alias', label: 'Alias' },
]


/** 편집 모드를 열 자리 — 그 디바이스 탭, 행·칸을 주면 그 칸으로 (검색 결과) */
export interface EditStart { deviceKey: string | null; row?: number | null; field?: string | null }

export default function EditMode({ spec, start, issues, showIssues, onToggleIssues, onClose, onNotice }: {
  spec: SpecDraft
  start?: EditStart
  issues: SpecIssues
  showIssues: boolean
  onToggleIssues: () => void
  onClose: () => void
  onNotice: (message: string, type?: 'success' | 'error') => void
}) {
  const { draft, setDraft } = spec
  const devices = draft.devices
  const [activeKey, setActiveKey] = useState<string | null>(
    (start?.deviceKey && devices.some(d => d.key === start.deviceKey) ? start.deviceKey : null) ?? devices[0]?.key ?? null)
  const activeIndex = Math.max(0, devices.findIndex(d => d.key === activeKey))
  const active = devices[activeIndex] ?? null
  const [showDup, setShowDup] = useState(false)
  const [panelOpen, setPanelOpen] = useState(false)
  const [popover, setPopover] = useState<'find' | 'help' | null>(null)
  const [renaming, setRenaming] = useState<string | null>(null)
  const [menu, setMenu] = useState<{ key: string; x: number; y: number } | null>(null)
  const gridHandle = useRef<TagGridHandle | null>(null)

  // 공동 편집 — 다른 사람이 있는 칸·탭, 내 자리 알리기 (탭을 옮기거나 서랍을 열면 그 디바이스, 칸을 고르면 그 칸)
  const { locate, viewers, me } = spec
  const activeDeviceKey = active?.key ?? null
  const others = useMemo(() => viewers.filter(v => v.connectionId !== me), [viewers, me])
  const tagRemote = useMemo(() => cellMarks(others, 'tags', v => v.cursor?.tag), [others])
  useEffect(() => {
    locate({ mode: panelOpen ? 'panel' : 'tags', device: activeDeviceKey })
  }, [locate, activeDeviceKey, panelOpen])
  useEffect(() => () => locate(null), [locate])

  const dups = useMemo(() => findDuplicates(devices), [devices])

  // 시트 높이 = 남은 화면 — 표 안에서 스크롤
  const sheetRef = useRef<HTMLDivElement>(null)
  const [sheetHeight, setSheetHeight] = useState(400)
  useEffect(() => {
    const el = sheetRef.current
    if (!el) return
    const ro = new ResizeObserver(([entry]) => setSheetHeight(Math.max(160, Math.floor(entry.contentRect.height))))
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // 검색 결과로 열었으면 그 칸으로 (표가 시트 높이에 맞춰 다시 그려진 뒤)
  useEffect(() => {
    if (start?.row == null || !start.field) return
    const t = setTimeout(() => gridHandle.current?.focusField(start.row!, start.field!), 80)
    return () => clearTimeout(t)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // 전체 화면 동안 뒤쪽 페이지 스크롤 막기
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  const setDevices = (update: (ds: DeviceSpecDevice[]) => DeviceSpecDevice[]) => setDraft(d => ({ ...d, devices: update(d.devices) }))
  const changeDevice = (key: string, update: (d: DeviceSpecDevice) => DeviceSpecDevice) =>
    setDevices(ds => ds.map(d => (d.key === key ? update(d) : d)))
  const setTags = (update: (tags: DeviceSpecTag[]) => DeviceSpecTag[]) => {
    if (active) changeDevice(active.key, d => ({ ...d, tags: update(d.tags) }))
  }

  const addDevice = () => {
    const device = { ...emptyDevice(), name: `디바이스 ${devices.length + 1}` }
    setDevices(ds => [...ds, device])
    setActiveKey(device.key)
  }
  const removeDevice = (key: string) => {
    const d = devices.find(x => x.key === key)
    if (d && d.tags.length > 0 && !confirm(`'${d.name || '디바이스'}' 와 태그 ${d.tags.length}개를 지울까요? (Ctrl+Z 로 되돌릴 수 있습니다)`)) return
    const i = devices.findIndex(x => x.key === key)
    setDevices(ds => ds.filter(x => x.key !== key))
    if (key === activeKey) setActiveKey(devices[i + 1]?.key ?? devices[i - 1]?.key ?? null)
  }
  const duplicate = (key: string) => {
    const { devices: next, copy } = duplicateDevice(devices, key)
    setDevices(() => next)
    if (copy) setActiveKey(copy.key)
  }

  // 찾기/바꾸기 — 툴바 버튼이나 Ctrl+F 로 연다. 여는 동안 표에서 커서를 빼 두어 [찾을 내용] 칸에 글자가 들어가게 하고,
  // 닫으면(어떻게 닫든) 커서를 돌려놓는다. 열려 있을 때 Ctrl+F 는 [찾을 내용] 칸으로 다시 가 글자를 선택한다
  const resumeGrid = useRef<(() => void) | null>(null)
  const [findFocus, setFindFocus] = useState(0)
  // 여러 칸을 고른 채 열면 그 영역만 바꿀 수 있다 (표에서 커서를 빼기 전에 잡아 둔다)
  const [findSel, setFindSel] = useState<FindSelection | null>(null)
  const [findMode, setFindMode] = useState<FindMode>('find')
  const openFind = (mode: FindMode = 'find') => {
    if (!resumeGrid.current) {
      const h = gridHandle.current
      setFindSel(h ? toFindSelection(active?.key, h.getSelection(), h.fieldAt) : null)
      resumeGrid.current = h?.release() ?? null
    }
    setPopover('find')
    setFindMode(mode)
    setFindFocus(n => n + 1)
  }
  useEffect(() => {
    if (popover === 'find' || !resumeGrid.current) return
    resumeGrid.current()
    resumeGrid.current = null
  }, [popover])
  // [정보 수정] 서랍이 열려 있는 동안도 표에서 커서를 뺀다 (서랍 입력칸에 글자가 가게)
  useEffect(() => {
    if (!panelOpen) return
    return gridHandle.current?.release()
  }, [panelOpen])
  const openFindRef = useRef(openFind)
  const panelOpenRef = useRef(panelOpen)
  useEffect(() => { openFindRef.current = openFind; panelOpenRef.current = panelOpen })
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase()
      if (!(e.ctrlKey || e.metaKey) || e.shiftKey || e.altKey || (k !== 'f' && k !== 'h') || panelOpenRef.current) return
      e.preventDefault() // 브라우저 찾기·방문 기록 대신
      openFindRef.current(k === 'h' ? 'replace' : 'find')
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Alt+↑/↓ · Alt(Ctrl)+PgUp/PgDn — 이전·다음 탭 (같은 칸 자리로), 지금 탭은 탭 줄 가운데로
  const { barRef: tabBarRef, startCell, select: selectTab } = useSheetTabs({
    keys: devices.map(d => d.key), activeKey: active?.key ?? null, setActiveKey,
    blocked: () => panelOpen || renaming != null,
    getCell: () => gridHandle.current?.getSelection()?.min ?? null,
  })

  // ── 행 조작 (지금 선택 기준) ──
  const insertRow = () => {
    const sel = gridHandle.current?.getSelection()
    const at = sel ? sel.max.row + 1 : active?.tags.length ?? 0
    setTags(tags => [...tags.slice(0, at), emptyTag(), ...tags.slice(at)])
    gridHandle.current?.selectRows(at, at)
  }
  const deleteRows = () => {
    const sel = gridHandle.current?.getSelection()
    if (!sel || !active) return onNotice('지울 행을 먼저 선택하세요.', 'error')
    const n = Math.min(sel.max.row, active.tags.length - 1) - sel.min.row + 1 // 아래 빈 행은 세지 않는다
    if (n <= 0) return
    setTags(tags => tags.filter((_, i) => i < sel.min.row || i > sel.max.row))
    onNotice(`${n}행을 지웠습니다. (Ctrl+Z 로 되돌리기)`, 'success')
  }
  const moveRows = (delta: -1 | 1) => {
    const sel = gridHandle.current?.getSelection()
    if (!sel || !active) return
    const { min, max } = { min: sel.min.row, max: sel.max.row }
    if (min + delta < 0 || max + delta >= active.tags.length) return
    setTags(tags => {
      const next = [...tags]
      const block = next.splice(min, max - min + 1)
      next.splice(min + delta, 0, ...block)
      return next
    })
    gridHandle.current?.selectRows(min + delta, max + delta)
  }

  // 찾기 범위 — 이 디바이스 · 모든 디바이스 · 선택 영역(고른 행·열 안에서만, 열 고르기와 겹치는 칸)
  const findScopes = [
    { key: 'tab', label: '이 디바이스' }, { key: 'all', label: '모든 디바이스' },
    ...(findSel ? [{ key: 'sel', label: selectionLabel(findSel) }] : []),
  ]
  const findRange = (scope: string, column: string) => {
    const sel = scope === 'sel' ? findSel : null
    const fields = (column === 'all' ? TEXT_FIELDS : [column as TextField]).filter(f => !sel || sel.fields.includes(f))
    const sheet = sel ? sel.sheetKey : active?.key
    return {
      fields,
      deviceIn: (d: DeviceSpecDevice) => scope === 'all' || d.key === sheet,
      rowIn: (i: number) => !sel || (i >= sel.rows[0] && i <= sel.rows[1]),
    }
  }
  // 수식 칸은 수식 원문에서 찾고 바꾼다 (값은 다시 계산된다). 대소문자는 가리지 않는다 — 수식의 {이름} 도 대소문자를 안 가린다
  const sourceOf = (t: DeviceSpecTag, f: TextField) => (f !== 'arithmetic' && t.formulas?.[f]) || t[f]
  // 찾은 칸 — 디바이스 순서, 행 순서, 시트 열 순서대로
  const findSearch = (pattern: RegExp, scope: string, column: string) => {
    const { fields, deviceIn, rowIn } = findRange(scope, column)
    const hits: FindHit[] = []
    for (const d of devices.filter(deviceIn)) d.tags.forEach((t, row) => {
      if (rowIn(row)) for (const field of fields) {
        const count = sourceOf(t, field).match(pattern)?.length ?? 0
        if (count) hits.push({ sheet: d.key, row, field, count })
      }
    })
    return hits
  }
  // only — [바꾸기] 로 그 칸만
  const findApply = (pattern: RegExp, replacement: string, scope: string, column: string, only?: FindHit) => {
    const { fields, deviceIn, rowIn } = findRange(scope, column)
    const count = (only ? [only] : findSearch(pattern, scope, column)).reduce((n, h) => n + h.count, 0)
    setDevices(ds => ds.map(d => (deviceIn(d) && (!only || d.key === only.sheet)
      ? {
        ...d, tags: d.tags.map((t, i) => {
          if (!rowIn(i) || (only && i !== only.row)) return t
          let next = t
          for (const f of fields) {
            if (only && f !== only.field) continue
            const before = sourceOf(next, f)
            const after = before.replace(pattern, () => replacement)
            // 그대로여도, 예전에 글자로 들어간 "=…" 값은 수식으로 살린다
            const strandedFormula = f !== 'arithmetic' && !next.formulas?.[f] && after.trim().startsWith('=')
            if (after === before && !strandedFormula) continue
            // 바꾼 결과가 = 로 시작하면 수식이 되고, 수식이 = 를 잃으면 값이 된다 (셀에 직접 쳐 넣은 것과 같게)
            next = f === 'arithmetic' ? { ...next, arithmetic: after }
              : setCellInput(next, f, after, f === 'address' ? v => normalizeAddress(channelDef(d.channelType), v) : v => v)
          }
          return next
        }),
      }
      : d)))
    if (!only) onNotice(`${count}곳을 바꿨습니다. (Ctrl+Z 로 되돌리기)`, 'success')
  }
  // 찾은 칸으로 — 다른 디바이스면 탭을 옮기고 표가 그려진 뒤
  const findGo = (hit: FindHit) => {
    if (hit.sheet === active?.key) return gridHandle.current?.focusField(hit.row, hit.field)
    setActiveKey(hit.sheet)
    setTimeout(() => gridHandle.current?.focusField(hit.row, hit.field), 80)
  }

  // 전송 전 점검 — [이전·다음 문제] 로 걸린 칸을 차례로 (디바이스 칸이면 [정보 수정] 서랍을 연다).
  // 처음엔 지금 커서 자리부터, 그다음은 방금 간 자리부터 (고친 칸은 목록에서 빠지므로 그대로 다음 칸이 나온다)
  const spots = useMemo(() => (showIssues ? issueSpots(devices, issues) : []), [showIssues, devices, issues])
  const lastSpot = useRef<IssueSpot | null>(null)
  useEffect(() => { lastSpot.current = null }, [showIssues])
  const goIssue = (delta: 1 | -1) => {
    const h = gridHandle.current
    const sel = h?.getSelection()
    const at = lastSpot.current ?? (active ? { deviceIndex: activeIndex, row: sel ? sel.min.row : null, field: sel ? h!.fieldAt(sel.min.col) ?? null : null } : null)
    const spot = nextSpot(spots, at, delta)
    if (!spot) return
    lastSpot.current = spot
    if (spot.row == null) {
      setActiveKey(spot.deviceKey)
      setPanelOpen(true) // 서랍에서 그 칸이 붉게, 이유가 칸 이름 옆에 보인다
    } else findGo({ sheet: spot.deviceKey, row: spot.row, field: spot.field, count: 1 })
  }

  const [note, setNote] = useState<CellNote | null>(null)

  const def = active ? channelDef(active.channelType) : undefined
  const tagCount = devices.reduce((n, d) => n + d.tags.filter(hasAddress).length, 0) // 저장되는 태그만

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-canvas">
      {/* 툴바 */}
      <div className="flex flex-wrap items-center gap-1 px-3 py-2 bg-surface border-b border-gray-200 shrink-0">
        <button onClick={onClose} title="편집 모드 나가기 (Ctrl+Shift+E)" className="flex items-center gap-1 px-2 py-1.5 rounded-md text-xs text-gray-600 hover:bg-gray-100">
          <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2}><path d="M10 3 5 8l5 5" strokeLinecap="round" strokeLinejoin="round" /></svg>
          나가기
        </button>
        <span className="text-sm font-semibold text-gray-800 truncate max-w-[220px] ml-1">{draft.name || '이름 없는 명세서'}</span>
        <span className="ml-2"><SaveBadge status={spec.status} savedAt={spec.savedAt} error={spec.errorMessage} onRetry={spec.saveNow} /></span>
        <span className="ml-2"><ViewerAvatars viewers={viewers} me={me} /></span>
        <Sep />
        <Tool label="되돌리기 (Ctrl+Z)" onClick={spec.undo} disabled={!spec.canUndo} d="M4 7h6a3 3 0 0 1 0 6H8M4 7l3-3M4 7l3 3" />
        <Tool label="다시 실행 (Ctrl+Y)" onClick={spec.redo} disabled={!spec.canRedo} d="M12 7H6a3 3 0 0 0 0 6h2M12 7 9 4M12 7l-3 3" />
        <Sep />
        <Tool label="아래에 행 삽입" onClick={insertRow} disabled={!active} text="행 삽입" />
        <Tool label="선택한 행 삭제" onClick={deleteRows} disabled={!active} text="행 삭제" />
        <Tool label="선택한 행 위로" onClick={() => moveRows(-1)} disabled={!active} d="M8 12V4M4.5 7.5 8 4l3.5 3.5" />
        <Tool label="선택한 행 아래로" onClick={() => moveRows(1)} disabled={!active} d="M8 4v8M4.5 8.5 8 12l3.5-3.5" />
        <Sep />
        <div className="relative">
          <Tool label="찾기 (Ctrl+F) / 바꾸기 (Ctrl+H)" onClick={() => (popover === 'find' ? setPopover(null) : openFind())} active={popover === 'find'} text="찾기/바꾸기" />
          {popover === 'find' && (
            <FindReplace key={findSel ? 'sel' : 'tab'} mode={findMode} onMode={setFindMode} scopes={findScopes} columns={FIND_COLUMNS} initialScope={findSel ? 'sel' : 'tab'}
              focusKey={findFocus} activeSheet={active?.key ?? null} search={findSearch} onReplace={findApply} onGo={findGo}
              onClose={() => setPopover(null)} />
          )}
        </div>
        <Tool label="겹치는 주소(디바이스 안)·Alias(전체)를 노랗게" onClick={() => setShowDup(s => !s)} active={showDup}
          text={showDup ? `중복 ${dups.count}` : '중복 표시'} />
        <Tool label="Edge 전송 전에 채울 칸 표시" onClick={onToggleIssues} active={showIssues}
          text={showIssues ? (issues.count === 0 ? '전송 준비 완료' : `문제 ${issues.count}곳`) : '전송 전 점검'} />
        {showIssues && issues.count > 0 && (
          <>
            <Tool label="이전 문제" onClick={() => goIssue(-1)} d="M10 3 5 8l5 5" />
            <Tool label="다음 문제" onClick={() => goIssue(1)} d="M6 3l5 5-5 5" text="다음 문제" />
          </>
        )}
        <div className="relative">
          <Tool label="수식·단축키 도움말" onClick={() => setPopover(p => (p === 'help' ? null : 'help'))} active={popover === 'help'} text="ƒx 도움말" />
          {popover === 'help' && <Help onClose={() => setPopover(null)} />}
        </div>
        <span className="ml-auto text-xs text-gray-400">디바이스 {devices.length} · 태그 {tagCount}</span>
      </div>

      {/* 디바이스 정보 줄 */}
      {active && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-xs text-gray-500 border-b border-gray-200 bg-surface/60 shrink-0">
          <span className="font-medium text-gray-800">{active.name || `디바이스 ${activeIndex + 1}`}</span>
          {[active.category, [active.maker, active.model].filter(Boolean).join(' ')].filter(Boolean).map((v, i) => <span key={i}>{v}</span>)}
          <span className={def ? 'text-gray-600' : 'text-gray-400'}>{def?.label ?? '프로토콜 미정'}</span>
          {def && endpointText(active) && <span className="font-mono text-gray-600">{endpointText(active)}</span>}
          <button onClick={() => setPanelOpen(true)} className="text-accent hover:underline">정보 수정</button>
          {def && <span className="ml-auto text-gray-400">주소: {def.addressHint}</span>}
        </div>
      )}

      {/* 시트 */}
      <div ref={sheetRef} className="flex-1 min-h-0 mx-3 mt-3 bg-surface rounded-t-lg border border-b-0 border-gray-200 overflow-hidden">
        {active ? (
          <TagGrid key={active.key} tags={active.tags} def={def} device={active} issues={issues} showIssues={showIssues}
            marks={showDup ? dups.marks : undefined} remote={tagRemote} height={sheetHeight} freeRows startCell={startCell}
            onChange={tags => changeDevice(active.key, d => ({ ...d, tags }))}
            onLocate={(tag, field, editing) => locate({ mode: 'tags', device: active.key, tag, field, editing })}
            onNote={setNote}
            handleRef={h => { gridHandle.current = h }}
            onNotice={m => onNotice(m, 'error')} />
        ) : (
          <div className="h-full flex flex-col items-center justify-center gap-3 text-sm text-gray-400">
            디바이스가 없습니다.
            <button onClick={addDevice} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-accent-hover">+ 디바이스 추가</button>
          </div>
        )}
      </div>

      {active && (
        <SheetStatus note={note} legend={['invalid', 'dup', 'formula', 'noaddr']}
          hint="Ctrl을 누른 채 채우기 핸들을 끌면 번호가 이어집니다 · 수식은 = 로 시작 (ƒx 도움말)" />
      )}

      {/* 탭 = 디바이스 */}
      <div ref={tabBarRef} className="relative flex items-stretch gap-0.5 px-3 bg-gray-100 border-t border-gray-200 shrink-0 overflow-x-auto [scrollbar-width:none] h-9">
        {devices.map((d, i) => (
          <div key={d.key} data-tab={d.key} draggable={renaming !== d.key}
            onDragStart={e => { e.dataTransfer.setData('text/device', d.key); e.dataTransfer.effectAllowed = 'move' }}
            onDragOver={e => { if (e.dataTransfer.types.includes('text/device')) e.preventDefault() }}
            onDrop={e => { const key = e.dataTransfer.getData('text/device'); if (key) setDevices(ds => moveDevice(ds, key, i)) }}
            onClick={() => selectTab(d.key)}
            onDoubleClick={() => setRenaming(d.key)}
            onContextMenu={e => { e.preventDefault(); setMenu({ key: d.key, x: e.clientX, y: e.clientY }) }}
            className={`flex items-center px-3 text-xs whitespace-nowrap cursor-pointer select-none border-x border-b-2 ${
              d.key === active?.key
                ? 'bg-surface text-gray-900 font-medium border-gray-200 border-b-accent -mt-px'
                : 'text-gray-500 border-transparent hover:bg-gray-50'
            }`}>
            {renaming === d.key ? (
              <input autoFocus defaultValue={d.name} onClick={e => e.stopPropagation()}
                onBlur={e => { changeDevice(d.key, x => ({ ...x, name: e.target.value.trim() || x.name })); setRenaming(null) }}
                onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setRenaming(null) }}
                className="w-32 border border-blue-400 rounded px-1 py-0.5 text-xs outline-none bg-surface" />
            ) : (
              <>
                {d.name || `디바이스 ${i + 1}`}
                <span className="ml-1.5 text-gray-500 font-normal">{d.tags.filter(hasAddress).length}</span>
                {showIssues && deviceIssueCount(d, issues) > 0 && (
                  <span className="ml-1.5 px-1 rounded bg-red-100 text-red-700 text-[10px] font-semibold leading-4" title="전송 전 점검에 걸린 칸">
                    {deviceIssueCount(d, issues)}
                  </span>
                )}
                <span className="ml-1.5 empty:hidden"><ViewerChips compact viewers={others.filter(v => v.cursor?.device === d.key)} /></span>
              </>
            )}
          </div>
        ))}
        <button onClick={addDevice} title="디바이스 추가" className="px-3 text-gray-500 hover:text-gray-800 hover:bg-gray-50 text-sm">+</button>
      </div>

      {menu && (
        <TabMenu x={menu.x} y={menu.y} onClose={() => setMenu(null)} items={[
          { label: '이름 바꾸기', action: () => setRenaming(menu.key) },
          { label: '정보 수정', action: () => { setActiveKey(menu.key); setPanelOpen(true) } },
          { label: '복제', action: () => duplicate(menu.key) },
          { label: '왼쪽으로', action: () => setDevices(ds => moveDevice(ds, menu.key, ds.findIndex(x => x.key === menu.key) - 1)) },
          { label: '오른쪽으로', action: () => setDevices(ds => moveDevice(ds, menu.key, ds.findIndex(x => x.key === menu.key) + 1)) },
          { label: '삭제', action: () => removeDevice(menu.key), danger: true },
        ]} />
      )}

      {/* 디바이스 정보 — 명세서 시트의 [설정] 서랍과 같은 것 */}
      {panelOpen && active && (
        <DeviceDrawer device={active} index={activeIndex} count={devices.length} issues={issues} showIssues={showIssues}
          onChange={update => changeDevice(active.key, update)}
          onMove={delta => setDevices(ds => moveDevice(ds, active.key, activeIndex + delta))}
          onDuplicate={() => { duplicate(active.key); setPanelOpen(false) }}
          onRemove={() => { removeDevice(active.key); setPanelOpen(false) }}
          onClose={() => setPanelOpen(false)} />
      )}
    </div>
  )
}

function Sep() {
  return <span className="w-px h-5 bg-gray-200 mx-1" />
}

// 툴바 버튼 — 누를 때 그리드가 '바깥 클릭'으로 선택을 풀지 않게 mousedown 을 막는다 (행 조작이 선택을 써야 함)
function Tool({ label, onClick, disabled, active, d, text }: {
  label: string
  onClick: () => void
  disabled?: boolean
  active?: boolean
  d?: string
  text?: string
}) {
  return (
    <button title={label} aria-label={label} onClick={onClick} disabled={disabled} onMouseDown={e => e.stopPropagation()}
      className={`flex items-center gap-1 px-2 py-1.5 rounded-md text-xs transition disabled:opacity-35 disabled:hover:bg-transparent ${
        active ? 'bg-blue-50 text-blue-700 ring-1 ring-blue-200' : 'text-gray-600 hover:bg-gray-100'
      }`}>
      {d && <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.6}><path d={d} strokeLinecap="round" strokeLinejoin="round" /></svg>}
      {text}
    </button>
  )
}

function Help({ onClose }: { onClose: () => void }) {
  const ref = useOutsideClose(onClose)
  const rows: [string, string][] = [
    ['=40001-40000', '사칙연산 (+ - * / 괄호) → 1'],
    ['=↑+1  =UP+1', '위 칸 + 1 (1-3x4 → 1-3x5, 110_CY2_PV → 110_CY3_PV)'],
    ['=[설명]  =[주소]', '같은 행 다른 칸'],
    ['=[설비구분] [제조사] [모델] [디바이스]', '이 디바이스 정보'],
    ['="110_CY"&ROW&"_PV"', '글자 잇기(&), ROW = 행 번호'],
    ['={code}_PV', '{이름} 자리에 값을 넣는 글자 틀 → CNC01_PV ({ROW}·{설비구분} 등도 됨)'],
  ]
  const keys: [string, string][] = [
    ['채우기 핸들 끌기', '그냥 끌면 복사 · Ctrl + 끌기는 자동 증가 (110_CY1_PV → CY2·CY3, ={code}_1 → _2·_3, ROW·↑ 수식은 그대로 이어 가기, 커서에 +)'],
    ['Ctrl + Shift + E', '편집 모드 들어가기 / 나오기'],
    ['F2', '칸 편집 (수식 칸이면 수식 원문)'],
    ['Ctrl + Z / Ctrl + Y', '되돌리기 / 다시 실행'],
    ['Alt + ↑ / ↓', '이전 / 다음 디바이스 탭 (Alt + PgUp / PgDn 도, 같은 칸 자리로)'],
    ['Ctrl + F / Ctrl + H', '찾기 / 바꾸기 (Enter 다음 칸 · Shift+Enter 이전, 여러 칸을 고른 채 열면 그 영역만, Esc 로 닫기)'],
    ['우클릭', '행 삽입·복제·삭제 (표), 탭 메뉴 (탭)'],
    ['Delete', '선택한 칸 지우기'],
  ]
  return (
    <div ref={ref} className="absolute right-0 top-full mt-1 z-50 w-[420px] bg-surface border border-gray-200 rounded-lg shadow-lg p-4 text-xs space-y-3">
      <div>
        <div className="font-medium text-gray-800 mb-1.5">수식은 '='로 시작하고, 칸을 벗어나면 계산된 값이 보입니다</div>
        <table className="w-full"><tbody>
          {rows.map(([f, d]) => <tr key={f}><td className="py-1 pr-3 font-mono text-gray-800 whitespace-nowrap">{f}</td><td className="py-1 text-gray-500">{d}</td></tr>)}
        </tbody></table>
        <p className="text-gray-400 mt-1">연산 칸은 LiveEdge 값 변환식(X*0.1) 자리라 수식으로 계산하지 않습니다.</p>
      </div>
      <div>
        <div className="font-medium text-gray-800 mb-1.5">단축키</div>
        <table className="w-full"><tbody>
          {keys.map(([k, d]) => <tr key={k}><td className="py-1 pr-3 text-gray-800 whitespace-nowrap">{k}</td><td className="py-1 text-gray-500">{d}</td></tr>)}
        </tbody></table>
      </div>
    </div>
  )
}
